import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { DialogActions, DialogContent } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { useBatchImportAction } from './batch-import-actions.hook';
import { Checkbox } from '../../../../../ui/components/pure/checkbox/checkbox.component';
import { PermissionBoundary } from '../../../../../core/permissions/permission-boundary.component';

export interface BatchImportUsersActionProps {
  currentProjectID: string;
  onAbort(): void;
  onSuccess(recordCount?: number): void;
}

export function BatchImportModal(props: BatchImportUsersActionProps) {
  const [state, handlers, userType, form] = useBatchImportAction(props);

  const archiveManagedUsersLabel = `Archive existing ${userType}s not in the uploaded data who are managed by an integration`;
  const archiveNonManagedUsersLabel = `Archive existing ${userType}s not in the uploaded data`;

  return (
    <Modal title={'Batch import'} open={true} loading={state.loading} maxWidth={'xs'} onClose={handlers.onCancelIntent}>
      <form onSubmit={form.handleSubmit(handlers.onSubmit)}>
        <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
          {state.current === 'BATCH_IMPORT_DENIED' && (
            <p>
              <Alert variant="outlined" severity="error">
                <AlertTitle>You cannot batch upload to this project.</AlertTitle>
                Please select another project and try again.
              </Alert>
            </p>
          )}
          {state.error && (
            <p>
              <Alert variant="outlined" severity="error">
                <AlertTitle>Something went wrong</AlertTitle>
                {parseErrorMessage(state.error.message)}
              </Alert>
            </p>
          )}
          Please download and complete the template spreadsheet below. CSV files can be edited in Microsoft Excel or
          Google Sheets.
          <p style={{ paddingTop: '12px' }}>
            <Alert variant="outlined" severity="info" style={{ width: 'auto' }}>
              <AlertTitle>
                The "external ID" field in the spreadsheet is your identifier for this user in your own database.
              </AlertTitle>
              It is through matching these IDs that we can identify existing users in the upload and prevent duplicates.
            </Alert>
          </p>
          <p style={{ paddingTop: '8px' }}>
            <TextButton color="primary" variant="outlined" onClick={() => handlers.onDownloadTemplate()}>
              Download CSV Template
            </TextButton>
          </p>
          <p>
            Then upload the completed CSV file here.
            {state.current !== 'BATCH_IMPORT_FILE_UPLOAD_SUCCESS' && (
              <p style={{ paddingTop: '8px' }}>
                <TextButton color="primary" variant="outlined" onClick={handlers.onUpload}>
                  Upload {userType}s
                </TextButton>
              </p>
            )}
            {state.current === 'BATCH_IMPORT_FILE_UPLOAD_FAILURE' && (
              <p style={{ paddingTop: '8px' }}>
                <Alert variant="outlined" severity="error">
                  <AlertTitle>There was an error uploading your file. Please try again.</AlertTitle>
                </Alert>
              </p>
            )}
            {state.current === 'BATCH_IMPORT_FILE_UPLOAD_SUCCESS' && (
              <p style={{ paddingTop: '8px' }}>
                <Alert variant="outlined" severity="info">
                  <AlertTitle>File uploaded: {state.fileName}</AlertTitle>
                  Please note that importing large files could take up to a few minutes.
                </Alert>
              </p>
            )}
            Options
            <p>
              <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
                <Checkbox control={form.control as any} name="isArchiveManagedUsers" label={archiveManagedUsersLabel} />
              </PermissionBoundary>
              <Checkbox
                control={form.control as any}
                name="isArchiveNonManagedUsers"
                label={archiveNonManagedUsersLabel}
              />
            </p>
          </p>
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={handlers.onCancelIntent}>
            Cancel
          </TextButton>
          <TextButton color="primary" variant="outlined" type="submit">
            Upload
          </TextButton>
        </DialogActions>
      </form>
    </Modal>
  );
}
